/**
 * Shared run-history table for the Integrations area. Plain MUI table - the
 * follow-on `integrations-framework-adoption` plan reuses it for the Skimmer /
 * Register detail pages, so it takes rows + an empty-state message and renders
 * nothing integration-specific.
 */
import React from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { format, parseISO } from 'date-fns'

import type { IntegrationRun } from '../../../../../services/admin/integrationsApi'
import { poolwerxColors } from '../../../../../theme/colors'
import { deriveRunDisplay } from './runStatus'
import { anyRunActive, useNow } from './usePollWhileRunning'

const formatStarted = (iso: string): string => {
  try {
    return format(parseISO(iso), 'd-MMM-yyyy HH:mm')
  } catch {
    return iso
  }
}

const formatDuration = (seconds: number | null): string => {
  if (seconds == null) return '-'
  if (seconds < 1) return '<1s'
  if (seconds < 60) return `${Math.round(seconds)}s`
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return `${mins}m ${secs}s`
}

/**
 * Detail cell that clamps to 3 lines with a Show more/less toggle - a failed
 * run's `detail` is the raw error (e.g. a full SQL statement) and must not blow
 * the row height open. The toggle only appears when the text actually overflows
 * the clamp, so short details ("Connection OK", count lines) render plainly.
 */
const RunDetailCell: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = React.useState(false)
  const [overflowing, setOverflowing] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    const el = ref.current
    if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1)
  }, [text])

  return (
    <Box sx={{ maxWidth: 420 }}>
      <Typography
        ref={ref}
        variant="body2"
        component="div"
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          ...(expanded
            ? {}
            : {
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
        }}
      >
        {text}
      </Typography>
      {(overflowing || expanded) && (
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          onClick={() => setExpanded((v) => !v)}
          sx={{ mt: 0.25 }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Link>
      )}
    </Box>
  )
}

interface IntegrationRunsTableProps {
  runs: IntegrationRun[]
  emptyMessage?: string
  /** Show a "Job" column (the integration key as a label) - for combined feeds. */
  showJob?: boolean
  /**
   * Friendly key -> label map for the "Job" column (combined feeds). Owned by the
   * consumer (e.g. the Freshdesk Status page) so this shared table stays
   * integration-agnostic. Falls back to the raw key for any unmapped run.
   */
  jobLabels?: Record<string, string>
  /** Cap the rendered rows (newest first) - for the compact inline section feed. */
  maxRows?: number
}

const IntegrationRunsTable: React.FC<IntegrationRunsTableProps> = ({
  runs,
  emptyMessage = 'No runs recorded yet.',
  showJob = false,
  jobLabels = {},
  maxRows,
}) => {
  const visible = maxRows ? runs.slice(0, maxRows) : runs
  // One ticking clock for the whole table, alive only while a run is running -
  // drives each running row's live elapsed without re-fetching.
  const now = useNow(anyRunActive(visible))

  if (runs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {emptyMessage}
      </Typography>
    )
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Started</TableCell>
            {showJob && <TableCell>Job</TableCell>}
            <TableCell>Trigger</TableCell>
            <TableCell>Initiated by</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Detail</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((run) => {
            const display = deriveRunDisplay(run)
            // While running, duration_seconds is null - show live elapsed from
            // started_at against the ticking clock.
            const elapsed = display.isRunning
              ? Math.max(0, (now - parseISO(run.started_at).getTime()) / 1000)
              : run.duration_seconds
            return (
              <TableRow key={run.id}>
                <TableCell>{formatStarted(run.started_at)}</TableCell>
                {showJob && (
                  <TableCell>
                    {jobLabels[run.integration_key] ?? run.integration_key}
                  </TableCell>
                )}
                <TableCell sx={{ textTransform: 'capitalize' }}>{run.trigger}</TableCell>
                <TableCell>{run.initiated_by || 'Scheduled'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Chip size="small" label={display.label} color={display.color} />
                    {display.isRunning && (
                      <CircularProgress
                        size={14}
                        sx={{ color: poolwerxColors.teal }}
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{formatDuration(elapsed)}</TableCell>
                <TableCell>
                  <RunDetailCell text={display.detail} />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default IntegrationRunsTable
